import { Alert, Button } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { useToast } from "../../../mantine/notifications/useToast";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import type {
  DirektvermarktungContractResponse,
  DirektvermarktungUnassignedGeneratorsResponse,
  GeneratorsSelectionFormValues
} from "../Direktvermarktung.types";
import { direktvermarktungContractsQueries } from "../hooks/useDirektvermarktungContracts";
import { useDirektvermarktungGeneratorShareMutations } from "../hooks/useDirektvermarktungGeneratorShareMutations";
import { useDirektvermarktungUnassignedGenerators } from "../hooks/useDirektvermarktungUnassignedGenerators";
import { VERMARKTUNG_CONTRACT_GENERATORS_SELECTION_FIELDS } from "./VermarktungContractGeneratorsSelection.constants";

interface VermarktungContractGeneratorsSelectionProps {
  createdContractId: DirektvermarktungContractResponse["id"];
  templateContract?: DirektvermarktungContractResponse;
  onClose: () => void;
}

function VermarktungContractGeneratorsSelection({
  createdContractId,
  templateContract,
  onClose
}: VermarktungContractGeneratorsSelectionProps) {
  const { control, handleSubmit, setValue } =
    useForm<GeneratorsSelectionFormValues>();

  const { showErrorToast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: unassignedGenerators,
    isLoading,
    error
  } = useDirektvermarktungUnassignedGenerators(
    {
      contractUuid: createdContractId
    },
    { enabled: !isSubmitting } // avoids that the generators disappear while saving them (one by one)
  );

  const generatorsOptions = getUnassignedGeneratorsInTemplate(
    unassignedGenerators ?? [],
    templateContract
  )?.map((g) => ({
    value: g.generator_id,
    label: g.generator_display_name
  }));

  useEffect(() => {
    if (Array.isArray(unassignedGenerators)) {
      setValue(
        "generators",
        getUnassignedGeneratorsInTemplate(
          unassignedGenerators,
          templateContract
        ).map((g) => g.generator_id)
      );
    }
  }, [unassignedGenerators, setValue, templateContract]);

  const { addGeneratorShareToVermarktungContractMutation } =
    useDirektvermarktungGeneratorShareMutations({
      contractUuid: createdContractId
    });

  async function handleConfirm(data: GeneratorsSelectionFormValues) {
    if (!data.generators) {
      onClose();
      return;
    }

    const generators = data.generators;
    const shares =
      templateContract?.generation_shares.filter((templateShare) =>
        generators?.some((g) => g === templateShare.generator_id)
      ) ?? [];

    if (shares.length === 0) {
      onClose();
      return;
    }

    setIsSubmitting(true);

    await Promise.all(
      shares.map((share) =>
        addGeneratorShareToVermarktungContractMutation
          .mutateAsync({
            contractId: createdContractId,
            formValues: {
              generator_id: share.generator_id,
              metering_or_market_location_id:
                share.metering_or_market_location_id
            }
          })
          .catch(() => {
            showErrorToast({
              message: `Der Erzeuger ${share.generator_display_name} (${share.generator_id}) konnte nicht zum Vertrag hinzugefügt werden.`
            });
          })
      )
    ).then(() =>
      queryClient.invalidateQueries({
        queryKey: direktvermarktungContractsQueries.all().queryKey
      })
    );

    setIsSubmitting(false);
    onClose();
  }

  return (
    <LoadOrError error={error} loading={isLoading}>
      {generatorsOptions && (
        <form onSubmit={handleSubmit(handleConfirm)}>
          {generatorsOptions.length > 0 ? (
            <FormFieldController
              control={control}
              data={{
                ...VERMARKTUNG_CONTRACT_GENERATORS_SELECTION_FIELDS.generators,
                data: generatorsOptions
              }}
            />
          ) : (
            <Alert bd="1px solid info" {...AlertPresets.info}>
              Keine Erzeuger verfügbar
            </Alert>
          )}
          <ModalFooter justify="flex-end">
            {generatorsOptions.length > 0 && (
              <Button variant="outline" onClick={onClose}>
                Weiter ohne Erzeuger
              </Button>
            )}
            <Button loading={isSubmitting} type="submit">
              {generatorsOptions.length > 0
                ? "Erzeuger übernehmen"
                : "Schließen"}
            </Button>
          </ModalFooter>
        </form>
      )}
    </LoadOrError>
  );
}

function getUnassignedGeneratorsInTemplate(
  unassignedGenerators: Array<DirektvermarktungUnassignedGeneratorsResponse>,
  templateContract?: DirektvermarktungContractResponse
) {
  if (!templateContract) {
    return [];
  }

  return unassignedGenerators.filter((generator) =>
    templateContract?.generation_shares.some(
      (ts) => ts.generator_id === generator.generator_id
    )
  );
}

export { VermarktungContractGeneratorsSelection };
